import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import customFetch from '../../../api/customFetch.js';

const ALL_SECTORS = [
  'אחמש', 'גבסן', 'הנהלה', 'טכאי אקו', 'טכנאי רנטגן',
  'מאמנים', 'מזכירות', 'מחשוב', 'מעבדה', 'רופאים', 'סיעוד', 'bio velvet',
];

const EMPTY_FORM = {
  title: '',
  description: '',
  textContent: '',
  fileUrl: '',
  fileName: '',
  fileType: '',
  targetSectors: [],
  isActive: true,
};

export default function RegulationEditor() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('text'); // 'text' | 'file'
  const fileInputRef = useRef();

  useEffect(() => {
    if (!isNew) loadRegulation();
  }, [id]);

  const loadRegulation = async () => {
    try {
      setLoading(true);
      const { data } = await customFetch.get(`/regulations/admin/${id}`);
      const reg = data.regulation;
      setForm({
        title: reg.title || '',
        description: reg.description || '',
        textContent: reg.textContent || '',
        fileUrl: reg.fileUrl || '',
        fileName: reg.fileName || '',
        fileType: reg.fileType || '',
        targetSectors: reg.targetSectors || [],
        isActive: reg.isActive ?? true,
      });
      if (reg.fileUrl) setActiveTab('file');
    } catch {
      setError('שגיאה בטעינת התקנון');
    } finally {
      setLoading(false);
    }
  };

  const handleField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const toggleSector = (sector) => {
    setForm((prev) => ({
      ...prev,
      targetSectors: prev.targetSectors.includes(sector)
        ? prev.targetSectors.filter((s) => s !== sector)
        : [...prev.targetSectors, sector],
    }));
  };

  const selectAllSectors = () => setForm((prev) => ({ ...prev, targetSectors: [...ALL_SECTORS] }));
  const clearSectors = () => setForm((prev) => ({ ...prev, targetSectors: [] }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowed.includes(file.type)) {
      setError('סוג קובץ לא נתמך. יש להעלות PDF או Word בלבד.');
      return;
    }

    const MAX_MB = 20;
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`הקובץ גדול מדי. מקסימום ${MAX_MB}MB.`);
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const { data } = await customFetch.post('/regulations/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setForm((prev) => ({
        ...prev,
        fileUrl: data.fileUrl,
        fileName: data.fileName,
        fileType: data.fileType,
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בהעלאת הקובץ');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('שם התקנון הוא שדה חובה');
      return;
    }
    if (form.targetSectors.length === 0) {
      setError('יש לבחור לפחות סקטור אחד');
      return;
    }
    if (!form.textContent.trim() && !form.fileUrl) {
      setError('יש להזין טקסט תקנון או להעלות קובץ');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      if (isNew) {
        await customFetch.post('/regulations/admin', form);
        setSuccess('התקנון נוצר בהצלחה!');
        setTimeout(() => nav('/admin/regulations'), 1200);
      } else {
        await customFetch.put(`/regulations/admin/${id}`, form);
        setSuccess('התקנון עודכן בהצלחה!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>טוען תקנון...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="page-title-icon">📋</span>
          {isNew ? 'תקנון חדש' : `עריכת תקנון: ${form.title}`}
        </h1>
        <div className="page-actions">
          <button className="admin-btn secondary" onClick={() => nav('/admin/regulations')}>
            ← חזור לרשימה
          </button>
          {!isNew && (
            <button className="admin-btn info" onClick={() => nav(`/admin/regulations/${id}/stats`)}>
              📊 מעקב קריאה
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="admin-alert error">
          <span>⚠️</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && (
        <div className="admin-alert success">
          <span>✅</span>
          <span>{success}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>
        {/* Main Form */}
        <div>
          {/* פרטי תקנון */}
          <div className="admin-card">
            <div className="card-header">
              <h3 className="card-title">פרטי התקנון</h3>
            </div>

            <div className="form-group">
              <label className="admin-label">שם התקנון *</label>
              <input
                type="text"
                className="admin-input"
                placeholder="לדוגמה: נוהל בטיחות 2024"
                value={form.title}
                onChange={(e) => handleField('title', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="admin-label">תיאור קצר</label>
              <input
                type="text"
                className="admin-input"
                placeholder="תיאור קצר של התקנון..."
                value={form.description}
                onChange={(e) => handleField('description', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="admin-label">סטטוס</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--admin-text)' }}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => handleField('isActive', e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  <span>{form.isActive ? '✅ פעיל - עובדים יראו תקנון זה' : '⏸️ לא פעיל - מוסתר מהעובדים'}</span>
                </label>
              </div>
            </div>
          </div>

          {/* תוכן התקנון */}
          <div className="admin-card" style={{ marginTop: 24 }}>
            <div className="card-header">
              <h3 className="card-title">תוכן התקנון</h3>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--admin-border)' }}>
              {[
                { key: 'text', label: '📄 טקסט', desc: 'הדבק תוכן טקסטואלי' },
                { key: 'file', label: '📎 קובץ', desc: 'PDF / Word' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderBottom: activeTab === tab.key ? '2px solid var(--admin-primary)' : '2px solid transparent',
                    background: 'transparent',
                    color: activeTab === tab.key ? 'var(--admin-primary)' : 'var(--admin-text-muted)',
                    cursor: 'pointer',
                    fontWeight: activeTab === tab.key ? 600 : 400,
                    fontSize: '0.95rem',
                    transition: 'all 0.2s',
                  }}
                >
                  {tab.label}
                  <span style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7 }}>{tab.desc}</span>
                </button>
              ))}
            </div>

            {/* Text tab */}
            {activeTab === 'text' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="admin-label">
                  טקסט התקנון
                  <span style={{ color: 'var(--admin-text-muted)', fontWeight: 400 }}> — הדבק את כל הטקסט כאן</span>
                </label>
                <textarea
                  className="admin-input"
                  placeholder="הדבק כאן את תוכן התקנון המלא...&#10;&#10;ניתן להשתמש בשורות חדשות להפרדה בין פסקאות."
                  value={form.textContent}
                  onChange={(e) => handleField('textContent', e.target.value)}
                  style={{ minHeight: 320, resize: 'vertical', lineHeight: 1.7, fontFamily: 'inherit' }}
                />
                {form.textContent && (
                  <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                    {form.textContent.length.toLocaleString()} תווים | {form.textContent.split('\n').filter(Boolean).length} שורות
                  </div>
                )}
              </div>
            )}

            {/* File tab */}
            {activeTab === 'file' && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />

                {form.fileUrl ? (
                  <div style={{
                    padding: 20,
                    background: 'var(--admin-card-hover)',
                    borderRadius: 10,
                    border: '1px solid var(--admin-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                  }}>
                    <span style={{ fontSize: '2rem' }}>
                      {form.fileType === 'pdf' ? '📕' : '📘'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--admin-text)' }}>{form.fileName}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                        {form.fileType?.toUpperCase()} | הועלה בהצלחה
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <a
                        href={form.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-btn secondary sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        👁️ פתח
                      </a>
                      <button
                        className="admin-btn warning sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        🔄 החלף
                      </button>
                      <button
                        className="admin-btn danger sm"
                        onClick={() => setForm((prev) => ({ ...prev, fileUrl: '', fileName: '', fileType: '' }))}
                      >
                        🗑️ הסר
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '2px dashed var(--admin-border)',
                      borderRadius: 12,
                      padding: 40,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                      color: 'var(--admin-text-muted)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--admin-primary)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--admin-border)')}
                  >
                    {uploading ? (
                      <>
                        <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
                        <div>מעלה קובץ...</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '3rem', marginBottom: 12 }}>📁</div>
                        <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--admin-text)' }}>
                          לחץ להעלאת קובץ
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>PDF או Word בלבד | מקסימום 10MB</div>
                      </>
                    )}
                  </div>
                )}

                <div style={{
                  marginTop: 16,
                  padding: 12,
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: 8,
                  fontSize: '0.85rem',
                  color: 'var(--admin-text-muted)',
                }}>
                  💡 <strong>טיפ:</strong> ניתן לצרף גם קובץ <strong>וגם</strong> טקסט — הטקסט יוצג ישירות,
                  והקובץ יהיה זמין להורדה/פתיחה.
                </div>

                {/* Also allow text even in file tab */}
                {form.fileUrl && (
                  <div className="form-group" style={{ marginTop: 20 }}>
                    <label className="admin-label">טקסט נוסף (אופציונלי)</label>
                    <textarea
                      className="admin-input"
                      placeholder="ניתן להוסיף טקסט תקציר או הערות..."
                      value={form.textContent}
                      onChange={(e) => handleField('textContent', e.target.value)}
                      style={{ minHeight: 120, resize: 'vertical' }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Sectors */}
        <div>
          <div className="admin-card" style={{ position: 'sticky', top: 20 }}>
            <div className="card-header">
              <h3 className="card-title">🏢 סקטורים מיועדים</h3>
            </div>

            <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
              <button className="admin-btn secondary sm" onClick={selectAllSectors}>
                ✅ בחר הכל
              </button>
              <button className="admin-btn secondary sm" onClick={clearSectors}>
                ✕ נקה
              </button>
            </div>

            <div style={{ marginBottom: 12, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>
              נבחרו {form.targetSectors.length} מתוך {ALL_SECTORS.length} סקטורים
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ALL_SECTORS.map((sector) => {
                const isSelected = form.targetSectors.includes(sector);
                return (
                  <label
                    key={sector}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                      border: `1px solid ${isSelected ? 'var(--admin-primary)' : 'var(--admin-border)'}`,
                      transition: 'all 0.15s',
                      color: 'var(--admin-text)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSector(sector)}
                      style={{ width: 'auto', accentColor: 'var(--admin-primary)' }}
                    />
                    <span style={{ fontSize: '0.9rem' }}>{sector}</span>
                    {isSelected && <span style={{ marginRight: 'auto', color: 'var(--admin-primary)', fontSize: '0.8rem' }}>✓</span>}
                  </label>
                );
              })}
            </div>

            <div style={{ marginTop: 24 }}>
              <button
                className="admin-btn primary"
                style={{ width: '100%' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '⏳ שומר...' : isNew ? '➕ צור תקנון' : '💾 שמור שינויים'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
